import React, { useEffect, useState } from "react";
import { createSearchParams, useNavigate } from "react-router-dom";
import classNames from "classnames";
import { GET_PARAMS, GET_POPUP_TYPES } from "../../../constants/popupConfig";
import InviteFriendLink from "../../molecules/InviteFriendLink";
import InviteFriendBonus from "../../molecules/InviteFriendBonus";
import InviteFriendEmpty from "../../molecules/InviteFriendEmpty";
import InviteFriendRules from "../../molecules/InviteFriendRules";
import InviteFriendBanner from "../../molecules/InviteFriendBanner";
import InviteFriendLinkMob from "../../molecules/InviteFriendLinkMob";
import InviteFriendTable from "../../molecules/InviteFriendTable";
import useTranslate from "../../../hooks/useTranslate";
import { useBreakpoint } from "../../../hooks/useBreakpoint";
import { useGTM } from "../../../hooks/useGTM";
import { ReferralItemType } from "../../../types/Referral";
import SkeletonInviteLink from "../../molecules/SkeletonInviteLink";
import SkeletonInviteTable from "../../molecules/SkeletonInviteTable";
import PageTitle from "../../atoms/PageTitle";
import useScrollDetection from "../../../hooks/useScrollDetection";

import "./styles.sass";

type InviteFriendProps = {
  link: string;
  amount: number;
  listReferral: ReferralItemType[];
  lastPage: number;
  total: number;
  currentPage: number;
  onPageChange: (selectedItem: { selected: number }) => void;
  isLoading: boolean;
  balance: number;
};

const InviteFriend = (props: InviteFriendProps) => {
  const {
    link,
    amount,
    listReferral,
    lastPage,
    total,
    currentPage,
    onPageChange: handlePageChange,
    isLoading,
    balance,
  } = props;
  const t = useTranslate();
  const { isDesktop } = useBreakpoint();
  const navigate = useNavigate();
  const GTM = useGTM();
  const [show, setShow] = useState(false);
  const isScrolled = useScrollDetection();

  const handleClick = async () => {
    GTM.clickCopyLinkInviteFriend();
    try {
      await navigator.clipboard.writeText(link);
      setShow(true);
      setTimeout(() => {
        setShow(false);
      }, 3000);
    } catch (error) {
      /* empty */
    }
  };

  const handleBonusClick = () => {
    GTM.clickGetBonusInviteFriend();
    if (balance > 0) {
      navigate({
        search: `?&${createSearchParams({ [GET_PARAMS.POPUP]: GET_POPUP_TYPES.popup.invite })}`,
      });
    } else {
      navigate({
        search: `?&${createSearchParams({ [GET_PARAMS.POPUP]: GET_POPUP_TYPES.popup.inviteNull })}`,
      });
    }
  };

  const renderInviteFriendLink = () => {
    if (isLoading && !link && !amount) {
      return <SkeletonInviteLink />;
    }
    if (isDesktop) {
      return <InviteFriendLink onClick={handleClick} link={link} />;
    }
    return <InviteFriendLinkMob />;
  };

  const renderInviteFriendTable = () => {
    if (isLoading) {
      return <SkeletonInviteTable />;
    }
    if (listReferral.length) {
      return (
        <InviteFriendTable
          listReferral={listReferral}
          currentPage={currentPage}
          lastPage={lastPage}
          total={total}
          onPageChange={handlePageChange}
        />
      );
    }
    return <InviteFriendEmpty />;
  };

  useEffect(() => {
    window.scrollTo({
      top: -1,
      behavior: "smooth",
    });
  }, []);

  const HeaderClass = classNames("InviteFriend__header", {
    border: isScrolled,
  });

  return (
    <div className="InviteFriend">
      <div className={HeaderClass}>
        <PageTitle
          title="profile.account.invite"
          // eslint-disable-next-line react/jsx-handler-names
          onBack={isDesktop ? null : () => navigate(-1)}
        />
      </div>
      <InviteFriendBanner />
      <div className="InviteFriend-content">
        <div className="InviteFriend-content-main">
          <div className="InviteFriend-content-main-data">
            {renderInviteFriendLink()}
          </div>
          {!isDesktop ? (
            <div className="InviteFriend-content-right">
              <InviteFriendBonus onClick={handleBonusClick} amount={amount} />
            </div>
          ) : null}
          {show ? (
            <div className="InviteFriend-content-main-note">
              <span className="InviteFriend-content-main-note__text">
                {t("referral.link.notification")}
              </span>
            </div>
          ) : null}
          {renderInviteFriendTable()}
          <InviteFriendRules />
        </div>
        {isDesktop ? (
          <div className="InviteFriend-content-right">
            <InviteFriendBonus onClick={handleBonusClick} amount={amount} />
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default InviteFriend;
